import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const GeofenceContext = createContext(null);

export function calculateDistanceMeters(lat1, lon1, lat2, lon2) {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
}

export const DEFAULT_SOCIETY_GEOFENCE = {
    society_name: "Pyramid City 3",
    full_name: "Pyramid City 3 Owners Co-Operative Housing Society Maryadit",
    address: "Besa-Pipla Road, Pipla, Nagpur (Maharashtra) - 440 037",
    latitude: 21.0847,
    longitude: 79.0970,
    radius_meters: 1800,
    guard_strict: true,
    resident_warning: true,
    enabled: true,
};

export function GeofenceProvider({ children }) {
    const [societyConfig, setSocietyConfig] = useState(DEFAULT_SOCIETY_GEOFENCE);
    const [coords, setCoords] = useState(null);
    const [distanceMeters, setDistanceMeters] = useState(null);
    const [status, setStatus] = useState("idle"); // 'idle' | 'checking' | 'inside' | 'outside' | 'denied' | 'unavailable' | 'disabled'
    const [residentWarningDismissed, setResidentWarningDismissed] = useState(() => {
        try {
            return sessionStorage.getItem("geofence_resident_banner_dismissed") === "true";
        } catch {
            return false;
        }
    });
    const [lastChecked, setLastChecked] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    // Load society geofence config from backend
    const loadConfig = useCallback(async () => {
        try {
            const res = await api.get("/society/geofence");
            if (res.data) {
                setSocietyConfig((prev) => ({ ...prev, ...res.data }));
            }
        } catch (err) {
            // Keep default Pyramid City 3 config on network error
        }
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    // Check GPS Location
    const checkLocation = useCallback(() => {
        if (!societyConfig.enabled) {
            setStatus("disabled");
            return;
        }

        if (typeof window === "undefined" || !navigator.geolocation) {
            setStatus("unavailable");
            setErrorMessage("Geolocation is not supported by your browser");
            return;
        }

        setStatus("checking");
        setErrorMessage("");

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const userLat = pos.coords.latitude;
                const userLng = pos.coords.longitude;
                const accuracy = pos.coords.accuracy;

                setCoords({ latitude: userLat, longitude: userLng, accuracy });
                setLastChecked(new Date());

                const dist = calculateDistanceMeters(
                    userLat,
                    userLng,
                    societyConfig.latitude,
                    societyConfig.longitude
                );
                setDistanceMeters(dist);

                if (dist <= societyConfig.radius_meters) {
                    setStatus("inside");
                } else {
                    setStatus("outside");
                }
            },
            (err) => {
                setLastChecked(new Date());
                if (err.code === err.PERMISSION_DENIED) {
                    setStatus("denied");
                    setErrorMessage("Location permission was denied. Please allow location access.");
                } else if (err.code === err.POSITION_UNAVAILABLE) {
                    setStatus("unavailable");
                    setErrorMessage("Location signal is unavailable. Please check your GPS/Wi-Fi.");
                } else {
                    setStatus("unavailable");
                    setErrorMessage(err.message || "Failed to retrieve location.");
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 12000,
                maximumAge: 30000,
            }
        );
    }, [societyConfig]);

    // Auto-check on initial mount
    useEffect(() => {
        checkLocation();
    }, [checkLocation]);

    // Watch position in background when active
    useEffect(() => {
        if (typeof window === "undefined" || !navigator.geolocation || !societyConfig.enabled) {
            return;
        }

        let watchId = null;
        try {
            watchId = navigator.geolocation.watchPosition(
                (pos) => {
                    const userLat = pos.coords.latitude;
                    const userLng = pos.coords.longitude;
                    const accuracy = pos.coords.accuracy;
                    setCoords({ latitude: userLat, longitude: userLng, accuracy });

                    const dist = calculateDistanceMeters(
                        userLat,
                        userLng,
                        societyConfig.latitude,
                        societyConfig.longitude
                    );
                    setDistanceMeters(dist);

                    if (dist <= societyConfig.radius_meters) {
                        setStatus("inside");
                    } else {
                        setStatus("outside");
                    }
                },
                () => {},
                { enableHighAccuracy: false, maximumAge: 60000, timeout: 20000 }
            );
        } catch {}

        return () => {
            if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
            }
        };
    }, [societyConfig]);

    const dismissResidentWarning = () => {
        setResidentWarningDismissed(true);
        try {
            sessionStorage.setItem("geofence_resident_banner_dismissed", "true");
        } catch {}
    };

    const isInside = status === "inside" || societyConfig?.enabled === false || societyConfig?.guard_strict === false;

    return (
        <GeofenceContext.Provider
            value={{
                societyConfig,
                coords,
                distanceMeters,
                isInside,
                status,
                checking: status === "checking",
                errorMessage,
                lastChecked,
                checkLocation,
                residentWarningDismissed,
                dismissResidentWarning,
                refreshConfig: loadConfig,
            }}
        >
            {children}
        </GeofenceContext.Provider>
    );
}

export const useGeofence = () => {
    const ctx = useContext(GeofenceContext);
    if (!ctx) {
        throw new Error("useGeofence must be used within a GeofenceProvider");
    }
    return ctx;
};

